import React, { useState } from "react";
import { api } from "../lib/api";
import { Loader2, Anchor, Plane, Box, FileText, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../i18n/LanguageContext";

const StatusPill = ({ status, method, tStatus }) => {
  const cls =
    status === "Delivered"
      ? "status-Delivered"
      : status === "In Transit"
      ? "status-InTransit"
      : status === "Ready for Pickup" || status === "At Port"
      ? "status-AtPort"
      : status === "At Customs"
      ? "status-AtCustoms"
      : status === "At Warehouse"
      ? "status-Commissioned"
      : "status-Commissioned";
  return (
    <span
      data-testid="ledger-status-pill"
      className={`${cls} inline-block px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded-sm`}
    >
      {tStatus(status, method)}
    </span>
  );
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const Ledger = () => {
  const { t, tStatus } = useT();
  const [trackingId, setTrackingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/shipments/track/${trackingId.trim().toUpperCase()}`);
      setResult(data);
    } catch (err) {
      const msg = err?.response?.data?.detail ? t("ledger.error_unknown") : t("ledger.error_unknown");
      setError(msg);
      toast.error(t("ledger.error_unknown"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="ledger"
      data-testid="ledger-section"
      className="relative bv-grain"
      style={{ background: "#1B3022" }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-center mb-12">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3 ornament">
            {t("ledger.eyebrow")}
          </div>
          <h2 className="font-serif-bv text-4xl sm:text-5xl lg:text-6xl text-[#F5EFE0]">
            {t("ledger.title_1")} <span className="italic text-[#D4AF37]">{t("ledger.title_italic")}</span>
          </h2>
          <p className="mt-4 text-[#F5EFE0]/75 max-w-2xl mx-auto">
            {t("ledger.subtitle")}
          </p>
        </div>

        <form
          onSubmit={handleSearch}
          data-testid="ledger-form"
          className="relative max-w-3xl mx-auto"
        >
          {/* Royal Charter Card */}
          <div
            className="relative rounded-sm p-1.5"
            style={{
              background:
                "linear-gradient(135deg, #D4AF37 0%, #A88420 50%, #D4AF37 100%)",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.3)",
            }}
          >
            <div
              className="rounded-sm p-3 sm:p-3"
              style={{
                background:
                  "radial-gradient(ellipse at top, #233D2C 0%, #1B3022 60%, #0F1D14 100%)",
                border: "1px solid rgba(212,175,55,0.3)",
              }}
            >
              <div className="flex items-center justify-center gap-2 py-1">
                <span className="text-[#D4AF37] text-xs">❖</span>
                <span className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]/80 font-serif-bv">
                  Royal Charter
                </span>
                <span className="text-[#D4AF37] text-xs">❖</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 px-2 pb-2">
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                  placeholder={t("ledger.placeholder")}
                  data-testid="ledger-input"
                  className="flex-1 bg-transparent text-[#F5EFE0] placeholder-[#F5EFE0]/40 px-5 py-4 outline-none tabular tracking-widest text-base border-b sm:border-b-0 sm:border-r border-[#D4AF37]/30 focus:border-[#D4AF37]"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="ledger-submit-btn"
                  className="btn-gold rounded-sm px-8 py-4 text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {t("ledger.submit_btn")}
                </button>
              </div>
            </div>
          </div>
          <div className="text-center mt-4 text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]/60">
            {t("ledger.try_sample")} <button type="button" onClick={() => setTrackingId("HBS000000000001")} className="underline hover:text-[#D4AF37]">HBS000000000001</button>
            <span className="mx-2">•</span>
            <button type="button" onClick={() => setTrackingId("HBS000000000002")} className="underline hover:text-[#D4AF37]">HBS000000000002</button>
            <span className="mx-2">•</span>
            <button type="button" onClick={() => setTrackingId("HBS000000000003")} className="underline hover:text-[#D4AF37]">HBS000000000003</button>
          </div>
        </form>

        {error && (
          <div
            data-testid="ledger-error"
            className="mt-10 max-w-3xl mx-auto p-6 border border-[#C8102E]/50 bg-[#0F1D14]/70 rounded-sm text-center"
          >
            <p className="font-serif-bv italic text-2xl text-[#E5A07A]">{error}</p>
          </div>
        )}

        {result && (
          <div
            data-testid="ledger-result"
            className="mt-12 max-w-3xl mx-auto fade-up"
          >
            {/* Manifest header */}
            <div className="border border-[#D4AF37]/40 bg-[#0F1D14]/80 rounded-sm overflow-hidden">
              <div className="border-b border-[#D4AF37]/30 bg-[#0F1D14] px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70">{t("ledger.manifest")}</div>
                  <div className="font-serif-bv text-2xl text-[#D4AF37] tabular">{result.shipment.tracking_id}</div>
                </div>
                <StatusPill status={result.shipment.current_status} method={result.shipment.method} tStatus={tStatus} />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-5 border-b border-[#D4AF37]/20">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/50">{t("ledger.sender")}</div>
                  <div className="text-sm text-[#F5EFE0] mt-1">{result.shipment.sender_name}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/50">{t("ledger.receiver")}</div>
                  <div className="text-sm text-[#F5EFE0] mt-1">{result.shipment.receiver_name}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/50">{t("ledger.cargo")}</div>
                  <div className="text-sm text-[#F5EFE0] mt-1 flex items-center gap-2">
                    {result.shipment.cargo_type === "Documents" ? (
                      <FileText size={14} className="text-[#D4AF37]" />
                    ) : (
                      <Box size={14} className="text-[#D4AF37]" />
                    )}
                    {result.shipment.cargo_type}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/50">{t("ledger.method")}</div>
                  <div className="text-sm text-[#F5EFE0] mt-1 flex items-center gap-2">
                    {result.shipment.method === "Air Freight" ? (
                      <Plane size={14} className="text-[#D4AF37]" />
                    ) : (
                      <Anchor size={14} className="text-[#D4AF37]" />
                    )}
                    {result.shipment.method}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 flex items-center gap-3 text-sm text-[#F5EFE0]/85">
                <span className="font-serif-bv text-lg text-[#D4AF37]">{result.shipment.origin_city}</span>
                <span className="flex-1 mx-2 h-px bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/40 to-[#D4AF37]" />
                <span className="font-serif-bv text-lg text-[#D4AF37]">{result.shipment.destination_city}</span>
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-5 ornament text-center">{t("ledger.journey")}</div>
              <ol data-testid="ledger-timeline" className="relative border-l border-[#D4AF37]/40 ml-3 space-y-7">
                {result.milestones.length === 0 && (
                  <li className="pl-6 text-[#F5EFE0]/70 text-sm">{t("ledger.no_milestones")}</li>
                )}
                {result.milestones.slice().reverse().map((m, idx) => (
                  <li key={m.update_id} className="pl-8 relative" data-testid={`milestone-${idx}`}>
                    <span
                      className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full ${
                        idx === 0 ? "bg-[#D4AF37] ring-4 ring-[#D4AF37]/25" : "bg-[#0F1D14] border-2 border-[#D4AF37]"
                      }`}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                      <div className="flex items-center gap-2 text-[#D4AF37] font-serif-bv text-lg">
                        <MapPin size={14} /> {m.location}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#F5EFE0]/60">
                        <Clock size={12} /> {formatTime(m.timestamp)}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-[#F5EFE0]/85">{m.notes}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Ledger;
